import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import * as authApi from '../services/authApi';
import { DASHBOARD_WIDGETS } from '../config/dashboardWidgets';

const emptyUserForm = {
  username: '',
  displayName: '',
  email: '',
  password: '',
  roleId: 'limited',
  active: true,
};

function UserManagement() {
  const { canManageUsers, canManageRoles, user: currentUser } = useAuth();
  const [tab, setTab] = useState(0);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState(emptyUserForm);
  const [roleEdits, setRoleEdits] = useState({});

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [usersData, rolesData] = await Promise.all([
        authApi.fetchUsers(),
        authApi.fetchRoles(),
      ]);
      setUsers(usersData);
      setRoles(rolesData);
      const edits = {};
      rolesData.forEach((role) => {
        edits[role.id] = {
          label: role.label,
          description: role.description || '',
          widgets: role.widgets?.includes('*') ? ['*'] : [...(role.widgets || [])],
          allWidgets: role.widgets?.includes('*'),
        };
      });
      setRoleEdits(edits);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canManageUsers) {
      loadData();
    }
  }, [canManageUsers, loadData]);

  const roleOptions = useMemo(
    () =>
      roles.map((role) => ({
        id: role.id,
        label: role.label,
      })),
    [roles]
  );

  const openCreateUser = () => {
    setEditingUser(null);
    setUserForm(emptyUserForm);
    setUserDialogOpen(true);
  };

  const openEditUser = (user) => {
    setEditingUser(user);
    setUserForm({
      username: user.username,
      displayName: user.displayName || '',
      email: user.email || '',
      password: '',
      roleId: user.roleId,
      active: user.active !== false,
    });
    setUserDialogOpen(true);
  };

  const handleSaveUser = async () => {
    setError('');
    setSuccess('');
    try {
      const payload = {
        username: userForm.username.trim(),
        displayName: userForm.displayName.trim(),
        email: userForm.email.trim(),
        roleId: userForm.roleId,
        active: userForm.active,
      };
      if (userForm.password) {
        payload.password = userForm.password;
      }

      if (editingUser) {
        await authApi.updateUser(editingUser.id, payload);
        setSuccess('User updated');
      } else {
        if (!userForm.password) {
          setError('Password is required for new users');
          return;
        }
        payload.password = userForm.password;
        await authApi.createUser(payload);
        setSuccess('User created');
      }
      setUserDialogOpen(false);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to save user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Delete this user?')) return;
    setError('');
    try {
      await authApi.deleteUser(userId);
      setSuccess('User deleted');
      await loadData();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to delete user');
    }
  };

  const toggleRoleWidget = (roleId, widgetId) => {
    setRoleEdits((prev) => {
      const current = prev[roleId];
      if (!current) return prev;
      if (current.allWidgets) return prev;

      const widgets = new Set(current.widgets);
      if (widgets.has(widgetId)) {
        widgets.delete(widgetId);
      } else {
        widgets.add(widgetId);
      }
      return {
        ...prev,
        [roleId]: { ...current, widgets: Array.from(widgets) },
      };
    });
  };

  const toggleRoleAllWidgets = (roleId, checked) => {
    setRoleEdits((prev) => ({
      ...prev,
      [roleId]: {
        ...prev[roleId],
        allWidgets: checked,
        widgets: checked ? ['*'] : [],
      },
    }));
  };

  const handleSaveRole = async (roleId) => {
    if (!canManageRoles) return;
    setError('');
    setSuccess('');
    try {
      const edit = roleEdits[roleId];
      await authApi.updateRole(roleId, {
        label: edit.label,
        description: edit.description,
        widgets: edit.allWidgets ? ['*'] : edit.widgets,
      });
      setSuccess(`Role "${edit.label}" permissions saved`);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to save role');
    }
  };

  const userColumns = [
    { field: 'username', headerName: 'Username', flex: 1, minWidth: 120 },
    { field: 'displayName', headerName: 'Display name', flex: 1, minWidth: 140 },
    { field: 'email', headerName: 'Email', flex: 1, minWidth: 160 },
    {
      field: 'roleId',
      headerName: 'Role',
      width: 140,
      renderCell: (params) => {
        const role = roles.find((r) => r.id === params.value);
        return <Chip size="small" label={role?.label || params.value} />;
      },
    },
    {
      field: 'active',
      headerName: 'Active',
      width: 90,
      renderCell: (params) => (
        <Chip
          size="small"
          color={params.value !== false ? 'success' : 'default'}
          label={params.value !== false ? 'Yes' : 'No'}
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 110,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box>
          <IconButton size="small" onClick={() => openEditUser(params.row)} aria-label="Edit user">
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDeleteUser(params.row.id)}
            disabled={params.row.id === currentUser?.id}
            aria-label="Delete user"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  if (!canManageUsers) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">You do not have permission to manage users.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight={600} gutterBottom>
        User Management
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Create accounts and control which dashboard widgets each role can see.
      </Typography>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      ) : null}
      {success ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      ) : null}

      <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 2 }}>
        <Tab label="Users" />
        {canManageRoles ? <Tab label="Role permissions" /> : null}
      </Tabs>

      {tab === 0 ? (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateUser}>
                Add user
              </Button>
            </Box>
            <Box sx={{ height: 520 }}>
              <DataGridPro
                rows={users}
                columns={userColumns}
                loading={loading}
                getRowId={(row) => row.id}
                disableRowSelectionOnClick
                pageSizeOptions={[10, 25, 50]}
                initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
              />
            </Box>
          </CardContent>
        </Card>
      ) : null}

      {tab === 1 && canManageRoles ? (
        <Grid container spacing={3}>
          {roles.map((role) => {
            const edit = roleEdits[role.id];
            if (!edit) return null;
            const widgetsByCategory = DASHBOARD_WIDGETS.reduce((acc, widget) => {
              if (!acc[widget.category]) acc[widget.category] = [];
              acc[widget.category].push(widget);
              return acc;
            }, {});

            return (
              <Grid item xs={12} key={role.id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                      <TextField
                        label="Role label"
                        value={edit.label}
                        onChange={(e) =>
                          setRoleEdits((prev) => ({
                            ...prev,
                            [role.id]: { ...prev[role.id], label: e.target.value },
                          }))
                        }
                        sx={{ minWidth: 200 }}
                      />
                      <TextField
                        label="Description"
                        value={edit.description}
                        onChange={(e) =>
                          setRoleEdits((prev) => ({
                            ...prev,
                            [role.id]: { ...prev[role.id], description: e.target.value },
                          }))
                        }
                        sx={{ flex: 1, minWidth: 240 }}
                      />
                    </Box>

                    <FormControlLabel
                      control={
                        <Switch
                          checked={edit.allWidgets}
                          onChange={(e) => toggleRoleAllWidgets(role.id, e.target.checked)}
                        />
                      }
                      label="Full dashboard access (all widgets)"
                      sx={{ mb: 2 }}
                    />

                    {!edit.allWidgets ? (
                      <Box>
                        {Object.entries(widgetsByCategory).map(([category, widgets]) => (
                          <Box key={category} sx={{ mb: 2 }}>
                            <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600 }}>
                              {category}
                            </FormLabel>
                            <FormGroup row>
                              {widgets.map((widget) => (
                                <FormControlLabel
                                  key={widget.id}
                                  control={
                                    <Checkbox
                                      checked={edit.widgets.includes(widget.id)}
                                      onChange={() => toggleRoleWidget(role.id, widget.id)}
                                    />
                                  }
                                  label={widget.label}
                                />
                              ))}
                            </FormGroup>
                            <Divider sx={{ mt: 1 }} />
                          </Box>
                        ))}
                      </Box>
                    ) : null}

                    <Button variant="contained" onClick={() => handleSaveRole(role.id)} sx={{ mt: 2 }}>
                      Save {edit.label} permissions
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      ) : null}

      <Dialog open={userDialogOpen} onClose={() => setUserDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingUser ? 'Edit user' : 'Add user'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Username"
            value={userForm.username}
            onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
            sx={{ mt: 1, mb: 2 }}
            disabled={!!editingUser}
          />
          <TextField
            fullWidth
            label="Display name"
            value={userForm.displayName}
            onChange={(e) => setUserForm({ ...userForm, displayName: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Email"
            value={userForm.email}
            onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="user-role-label">Role</InputLabel>
            <Select
              labelId="user-role-label"
              label="Role"
              value={userForm.roleId}
              onChange={(e) => setUserForm({ ...userForm, roleId: e.target.value })}
            >
              {roleOptions.map((role) => (
                <MenuItem
                  key={role.id}
                  value={role.id}
                  disabled={!canManageRoles && role.id === 'superadmin'}
                >
                  {role.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            type="password"
            label={editingUser ? 'New password (leave blank to keep)' : 'Password'}
            value={userForm.password}
            onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={userForm.active}
                onChange={(e) => setUserForm({ ...userForm, active: e.target.checked })}
              />
            }
            label="Active"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveUser}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default UserManagement;
